/**
 * Μικρά κοινά κομμάτια της σελίδας «Κατάλογος» (διακόπτης, αναδυόμενο μενού, κουκκίδα τόνου,
 * πεδίο ποσού). Τα χρώματα/σχήματα είναι στο Catalog.css.
 */
import React, { useEffect, useRef, useState } from 'react';
import { catalogTone } from '../../utils/catalogColors';
import { fmtDecimal, parseDecimal } from './catalogModel';

export const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

interface SwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  pending?: boolean;
  disabled?: boolean;
  title?: string;
  id?: string;
  labelledBy?: string;
}

export const Switch: React.FC<SwitchProps> = ({ checked, onChange, label, pending, disabled, title, id, labelledBy }) => (
  <button
    type="button"
    role="switch"
    id={id}
    aria-checked={checked}
    aria-label={labelledBy ? undefined : label}
    aria-labelledby={labelledBy}
    title={title ?? label}
    disabled={disabled}
    className={cx('cx-switch', pending && 'pending')}
    onClick={e => { e.stopPropagation(); onChange(!checked); }}
  />
);

/** Κουκκίδα τόνου: ο τόνος (ρητός ή ο αυτόματος του ταμείου)· χωρίς τόνο = ουδέτερη. */
export const ToneDot: React.FC<{ tone?: string | null; large?: boolean; title?: string }> = ({ tone, large, title }) => {
  const valid = catalogTone(tone);
  return (
    <span
      aria-hidden={title ? undefined : true}
      title={title}
      data-tone={valid || undefined}
      data-testid="tone-dot"
      className={cx('cx-dot', valid && 'catalog-tone', large && 'lg')}
    />
  );
};

/** Κλείνει με κλικ έξω ή Escape. */
export function useDismiss(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) close(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open, close]);
  return ref;
}

export interface MenuItem {
  key: string;
  label: React.ReactNode;
  onSelect: () => void;
  disabled?: boolean;
  danger?: boolean;
  checked?: boolean;
  hint?: string;
  icon?: React.ReactNode;
}

interface MenuButtonProps {
  label: string;
  items: Array<MenuItem | 'sep' | { heading: string }>;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  align?: 'left' | 'right';
  title?: string;
  /** role του κουμπιού: radio = επιλογή μίας τιμής (φίλτρο) */
  kind?: 'menu' | 'radio';
}

/** Κουμπί που ανοίγει λίστα επιλογών (⋯, φίλτρο, «Προσθήκη ομάδας»). */
export const MenuButton: React.FC<MenuButtonProps> = ({ label, items, children, className, disabled, align = 'left', title, kind = 'menu' }) => {
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        className={className}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={title ?? label}
        disabled={disabled}
        onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
      >
        {children}
      </button>
      {open && (
        <div role="menu" aria-label={label} className="cx-menu" style={align === 'left' ? { top: 'calc(100% + 4px)', left: 0 } : { top: 'calc(100% + 4px)', right: 0 }}>
          {items.map((item, i) => {
            if (item === 'sep') return <div key={`sep${i}`} className="cx-menu-sep" />;
            if ('heading' in item) return <div key={`h${i}`} className="cx-menu-h">{item.heading}</div>;
            return (
              <button
                key={item.key}
                type="button"
                role={kind === 'radio' ? 'menuitemradio' : 'menuitem'}
                aria-checked={kind === 'radio' ? !!item.checked : undefined}
                disabled={item.disabled}
                className={item.danger ? 'danger' : undefined}
                onClick={e => { e.stopPropagation(); setOpen(false); item.onSelect(); }}
              >
                {item.icon}
                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>
                {item.hint && <span className="cx-menu-hint">{item.hint}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface MoneyInputProps {
  value: number | null;
  onChange: (next: number | null) => void;
  /** κενό επιτρέπεται (κανάλι = η «Τιμή») · αλλιώς κενό → 0 */
  nullable?: boolean;
  placeholder?: string;
  label: string;
  changed?: boolean;
  title?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
}

/**
 * Πεδίο ποσού με κόμμα («3,40»): κρατά ό,τι πληκτρολογείς όσο έχει την εστίαση και δίνει αριθμό
 * σε κάθε αλλαγή· εκτός εστίασης δείχνει το ποσό μορφοποιημένο.
 */
export const MoneyInput: React.FC<MoneyInputProps> = ({ value, onChange, nullable, placeholder, label, changed, title, disabled, className, id }) => {
  const [text, setText] = useState<string | null>(null);
  const shown = text ?? fmtDecimal(value);
  return (
    <input
      id={id}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      aria-label={label}
      title={title}
      disabled={disabled}
      placeholder={placeholder}
      className={cx('cx-inp sm num', changed && 'cx-changed', className)}
      value={shown}
      onFocus={() => setText(fmtDecimal(value))}
      onBlur={() => setText(null)}
      onChange={e => {
        const raw = e.target.value;
        setText(raw);
        if (raw.trim() === '') { onChange(nullable ? null : 0); return; }
        // Άκυρο κείμενο (π.χ. γράμμα) δεν αλλάζει το ποσό.
        const n = parseDecimal(raw);
        if (n !== null) onChange(n);
      }}
    />
  );
};
